import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Normal auth check — use cookie or Bearer token from localStorage
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const exchangeSession = useCallback(async (sessionId) => {
    try {
      console.log('[AUTH] Exchanging session_id:', sessionId?.substring(0, 20) + '...');
      const res = await api.get(`/auth/session?session_id=${sessionId}`);
      console.log('[AUTH] Exchange success:', res.data?.email);
      if (res.data?.session_token) {
        localStorage.setItem('session_token', res.data.session_token);
      }
      setUser(res.data);
      return true;
    } catch (err) {
      console.error('[AUTH] Session exchange failed:', err?.response?.status, err?.response?.data || err.message);
      return false;
    }
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch { /* ignore */ }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, exchangeSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
